import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { PlusCircle, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ManualWalletEntryDialog({ token, defaultCustomerId = '', onDone, triggerLabel = 'Offline Entry' }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    customer_id: defaultCustomerId,
    wallet_type: 'ott',
    direction: 'credit',
    amount: '',
    reason: '',
    reference: '',
  });

  const submit = async () => {
    if (!form.customer_id || !form.amount || !form.reason) {
      toast.error('customer_id, amount and reason are required');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/wallet/manual-entry`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, amount: parseFloat(form.amount) }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Failed');
        return;
      }
      toast.success(
        `${form.direction === 'credit' ? 'Credited' : 'Debited'} Rs.${form.amount}. Balance: Rs.${data.before_balance} → Rs.${data.after_balance}`
      );
      setOpen(false);
      setForm({ ...form, amount: '', reason: '', reference: '' });
      onDone?.();
    } catch (e) {
      toast.error('Failed: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="open-manual-entry-btn">
          <PlusCircle className="h-4 w-4 mr-2" /> {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Offline / Manual Wallet Entry</DialogTitle>
          <DialogDescription>
            Record a cash, cheque, bank transfer, or any offline adjustment.
            The ledger, wallet balance and audit trail all update in one shot.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Customer ID</Label>
            <Input
              value={form.customer_id}
              onChange={(e) => setForm({ ...form, customer_id: e.target.value })}
              placeholder="CUST-xxxxxx"
              data-testid="me-customer-id"
              disabled={!!defaultCustomerId}
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Wallet</Label>
              <Select value={form.wallet_type} onValueChange={(v) => setForm({ ...form, wallet_type: v })}>
                <SelectTrigger data-testid="me-wallet-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ott">OTT</SelectItem>
                  <SelectItem value="iptv">IPTV</SelectItem>
                  <SelectItem value="railtel">Railtel</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Direction</Label>
              <Select value={form.direction} onValueChange={(v) => setForm({ ...form, direction: v })}>
                <SelectTrigger data-testid="me-direction"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="credit">Credit (+)</SelectItem>
                  <SelectItem value="debit">Debit (−)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Amount (Rs.)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                data-testid="me-amount"
              />
            </div>
          </div>
          <div>
            <Label>Reason / Note</Label>
            <Textarea
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              placeholder="Cash received in office / Cheque #123 / Refund for cancelled subscription…"
              rows={2}
              data-testid="me-reason"
            />
          </div>
          <div>
            <Label>Reference (optional)</Label>
            <Input
              value={form.reference}
              onChange={(e) => setForm({ ...form, reference: e.target.value })}
              placeholder="Cheque no, receipt no, bank ref…"
              data-testid="me-reference"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="me-submit-btn">
            {saving ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Saving…</> : 'Record Entry'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
